import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Additional Discount master (Masters → Additional Discounts / Upsell). Fixed
 * rupee amount applied on the itinerary Pricing step. `percent` is legacy
 * (nullable) - existing rows keep whatever percent they were saved with; new
 * rows use `amountPaise`.
 */

/** Paise as a non-negative decimal string (BigInt-safe over JSON). */
const paiseString = z.string().regex(/^\d+$/, 'must be a non-negative integer paise string');

export const AdditionalDiscountListItemSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
  /** Fixed upsell amount in paise. */
  amountPaise: paiseString,
  /** Legacy percent off final markup (e.g. 5 = 5%). Null for fixed-amount rows. */
  percent: z.number().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type AdditionalDiscountListItem = z.infer<typeof AdditionalDiscountListItemSchema>;

export const AdditionalDiscountListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type AdditionalDiscountListQuery = z.infer<typeof AdditionalDiscountListQuerySchema>;

export const AdditionalDiscountListResponseSchema = paginatedResponseSchema(
  AdditionalDiscountListItemSchema,
);
export type AdditionalDiscountListResponse = z.infer<typeof AdditionalDiscountListResponseSchema>;

export const AdditionalDiscountDetailSchema = AdditionalDiscountListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type AdditionalDiscountDetail = z.infer<typeof AdditionalDiscountDetailSchema>;

const AdditionalDiscountCoreFieldsSchema = z.object({
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(120),
  amountPaise: paiseString,
  /** Legacy percent field - not written by the current UI, kept for back-compat. */
  percent: z.number().min(0).max(100).nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateAdditionalDiscountRequestSchema = AdditionalDiscountCoreFieldsSchema;
export type CreateAdditionalDiscountRequest = z.infer<typeof CreateAdditionalDiscountRequestSchema>;

export const UpdateAdditionalDiscountRequestSchema = AdditionalDiscountCoreFieldsSchema.partial();
export type UpdateAdditionalDiscountRequest = z.infer<typeof UpdateAdditionalDiscountRequestSchema>;

export const AdditionalDiscountRefSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
  amountPaise: paiseString,
  percent: z.number().nullable(),
});
export type AdditionalDiscountRef = z.infer<typeof AdditionalDiscountRefSchema>;
